import { CONTENT_FITNESS_ASSESSMENT_MODE } from "../../../enums/content-fitness";
import { CONTENT_FITNESS_FRAMEWORK_VERSION } from "../version";
import { anchors, type ContentFitnessSubcriterion, deferred } from "./types";

export const AI_AGENT_READINESS_SUBCRITERIA = [
  deferred(
    "ai_agent_readiness.crawlable_indexable",
    "Crawlable & indexable",
    "ai_agent_readiness",
    1,
    CONTENT_FITNESS_ASSESSMENT_MODE.HYBRID,
    "Scores whether the page is accessible to crawlers and AI agents and can be indexed where expected.",
  ),
  {
    key: "ai_agent_readiness.server_rendered_extractable_content",
    version: CONTENT_FITNESS_FRAMEWORK_VERSION,
    label: "Server-rendered, extractable content",
    parentMetricKey: "ai_agent_readiness",
    order: 2,
    assessmentMode: CONTENT_FITNESS_ASSESSMENT_MODE.HYBRID,
    pilot: true,
    definition:
      "Scores whether agents can fetch key content and facts as clean machine-readable text without relying on fragile scripts or interactions.",
    evidenceGuidance:
      "Use bounded source/rendered text, structured document evidence, screenshots, script dependence, hidden-content indicators and interaction-gated facts.",
    applicabilityNotes:
      "Do not infer extractability from screenshot appearance alone; use source/rendered text evidence where possible.",
    anchors: anchors(
      "Key content is unavailable to agents or locked behind scripts, images, auth or interactions.",
      "Only fragments of the key content are extractable; important facts depend on fragile rendering or interactions.",
      "Core content is partly extractable, but material facts or structure are missing from robust machine-readable text.",
      "Key content is reliably extractable with minor dependence on rendering or secondary interactions.",
      "Key facts are cleanly available to agents in robust, machine-readable text with strong render/source consistency.",
    ),
    objectiveChecks: [
      "key facts in source/rendered text",
      "body text extractable",
      "image-only facts",
      "script/interaction-gated facts",
    ],
  },
  deferred(
    "ai_agent_readiness.structured_data_meaning",
    "Structured data & meaning",
    "ai_agent_readiness",
    3,
    CONTENT_FITNESS_ASSESSMENT_MODE.HYBRID,
    "Scores whether structured data gives AI systems explicit, accurate meaning that matches visible content and page type.",
  ),
  deferred(
    "ai_agent_readiness.content_parity_no_cloaking",
    "Content parity",
    "ai_agent_readiness",
    4,
    CONTENT_FITNESS_ASSESSMENT_MODE.HYBRID,
    "Scores whether crawlers and users see materially consistent content without hidden, cloaked or divergent facts.",
  ),
  {
    key: "ai_agent_readiness.self_contained_citable_answers",
    version: CONTENT_FITNESS_FRAMEWORK_VERSION,
    label: "Self-contained, citable answers",
    parentMetricKey: "ai_agent_readiness",
    order: 5,
    assessmentMode: CONTENT_FITNESS_ASSESSMENT_MODE.RUBRIC,
    pilot: true,
    definition:
      "Scores whether the page answers likely AI/user questions clearly enough to be quoted and attributed to the brand.",
    evidenceGuidance:
      "Use body text, headings, claims, proof points, FAQs, product/service facts, citations and attribution cues.",
    applicabilityNotes:
      "Pages whose job is navigation may score on whether they provide citable orientation rather than deep answers. Do not claim authority without textual support.",
    anchors: anchors(
      "No usable answer or attributable information can be cited from the page.",
      "Answers are vague, fragmented, generic or unsupported, making citation risky.",
      "Some citable information exists, but important questions, proof or attribution are incomplete.",
      "The page provides clear, mostly self-contained answers with adequate proof and attribution.",
      "The page gives distinctive, complete, well-supported answers that AI systems can confidently quote and attribute to the brand.",
    ),
    objectiveChecks: [
      "direct answer present",
      "source/proof nearby",
      "named brand/entity",
      "unsupported superlative claims",
    ],
  },
  deferred(
    "ai_agent_readiness.multimodal_clarity",
    "Multimodal clarity",
    "ai_agent_readiness",
    6,
    CONTENT_FITNESS_ASSESSMENT_MODE.HYBRID,
    "Scores whether non-text content has enough titles, alt text, captions and context for machines to interpret it.",
  ),
  deferred(
    "ai_agent_readiness.freshness_stable_urls",
    "Freshness & stable URLs",
    "ai_agent_readiness",
    7,
    CONTENT_FITNESS_ASSESSMENT_MODE.HYBRID,
    "Scores whether content is current and served from a stable, canonical URL agents can cite.",
  ),
] as const satisfies readonly ContentFitnessSubcriterion[];
